import React from "react"
import Flex from "@irati/ui/src/Flex"
import {Card} from "@irati/ui/src/Layout"
import {ButtonLink} from "@irati/ui/src/Link"
import styled, {css} from "styled-components"
import FormattedMessage, {FormattedCurrency, useIntl} from "@irati/utils/lang"
import Markdown from "@irati/ui/src/Markdown"
import {Body} from "@irati/ui/src/Typography"
import {FlexiPointMigrationStatus, flexiConversionAmount} from "@irati/wallet/hooks/flexiMigration"
import {walletTracking} from "@irati-tracking/wallet"

const StyledCard = styled(Card)<Pick<Props, "migrationStatus">>`
  width: 100%;

  ${({theme, migrationStatus}) => {
    switch (migrationStatus) {
      case "POST":
        return `background: linear-gradient(241deg, ${theme.colors.secondary1} 0%, ${theme.colors.primary2} 100%);`
      case "MID":
        return `background: ${theme.colors.tertiary.o_400}`
      case "PRE":
      default:
        return `background: linear-gradient(61deg, ${theme.colors.primary2} 0%, ${theme.colors.primary1} 100%);`
    }
  }}
`

const Container = styled.div<Pick<Props, "responsive">>`
  text-align: center;

  ${({theme, responsive}) =>
    responsive &&
    css`
      display: flex;
      padding: ${theme.spacing.two};
      flex-direction: row;
      flex-wrap: wrap;
      gap: ${theme.spacing.two};

      & > :first-child {
        min-width: 250px;
      }
    `}
`

interface Props {
  amount: number
  migrationStatus: FlexiPointMigrationStatus
  isAdmin?: boolean
  /** if set, the Card will try to display with flex row */
  responsive?: boolean
}

const source = {
  PRE: "wallet.pointsConversion.rewardWallet.pre",
  MID: "wallet.pointsConversion.rewardWallet.during",
  POST: "wallet.pointsConversion.rewardWallet.post"
}

export function ConversionInfo({amount, responsive, migrationStatus, isAdmin}: Props) {
  const {formatMessage} = useIntl()
  return (
    <StyledCard noBorder tiny migrationStatus={migrationStatus}>
      <Container responsive={responsive}>
        <Flex alignItems="center" direction="column" flex="100">
          <Markdown
            source={source[migrationStatus]}
            className="reward-wallet__white--markdown"
            componentProps={{body: {small: true}}}
            values={{
              price: <PriceComparison amount={amount} migrationStatus={migrationStatus} />
            }}
          />
        </Flex>

        <Flex flex="1 1 fit-content" direction="column" justifyContent="center">
          <ButtonLink
            onClick={(e) => {
              walletTracking.clickPointsSimplifcationLearnMore({source: isAdmin ? "admin_wallet" : "user_wallet"})
              e.stopPropagation()
            }}
            full
            inverted
            small
            primary
            href={formatMessage({id: isAdmin ? "wallet.pointsConversion.learnAdminLink" : "wallet.pointsConversion.learnLink"})}
            target="_blank">
            <FormattedMessage id="platform.cta.learnMore" />
          </ButtonLink>
        </Flex>
      </Container>
    </StyledCard>
  )
}

function PriceComparison({amount, migrationStatus}: Props) {
  const {changed, current} = flexiConversionAmount(amount, migrationStatus)

  return (
    <Flex justifyContent="center" gap={4} margin={[2, 0]}>
      <Body small>
        <FormattedMessage
          id={
            migrationStatus === "POST"
              ? "wallet.pointsConversion.rewardWallet.price.updated"
              : "wallet.pointsConversion.rewardWallet.price.current"
          }
        />
        <Flex justifyContent="center">
          <FormattedCurrency currency="RP" value={current} />
        </Flex>
      </Body>
      {current > 0 && (
        <Body small>
          <FormattedMessage
            id={
              migrationStatus === "POST"
                ? "wallet.pointsConversion.rewardWallet.price.previous"
                : "wallet.pointsConversion.rewardWallet.price.update"
            }
          />
          <FormattedCurrency currency="RP" value={changed} />
        </Body>
      )}
    </Flex>
  )
}
